import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Mail, Phone, Building2, Trash2, RefreshCw, MousePointerClick,
  QrCode, Download, FileDown, BarChart3, TrendingUp, MailCheck,
  Send, X, History, Eye, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function getAuthHeaders() {
  const token = localStorage.getItem('admin_token');
  return { Authorization: `Bearer ${token}` };
}

const BIRD_BADGE = {
  Eagle:  { bg: 'bg-orange-100', text: 'text-orange-700', image: '/brand/eagle.png' },
  Parrot: { bg: 'bg-yellow-100', text: 'text-yellow-700', image: '/brand/parrot.png' },
  Dove:   { bg: 'bg-cyan-100',   text: 'text-cyan-700',   image: '/brand/dove.png' },
  Owl:    { bg: 'bg-indigo-100', text: 'text-indigo-700', image: '/brand/owl.png' },
};

const BirdMascot = ({ name, className = 'h-5 w-5' }) => {
  const meta = BIRD_BADGE[name];
  if (!meta?.image) return null;
  return (
    <img
      src={meta.image}
      alt={`${name} mascot`}
      className={`${className} object-contain inline-block align-middle`}
      decoding="async"
    />
  );
};

const QR_PRESETS = [
  { label: 'Workshop',         utm_medium: 'qr', utm_source: 'workshop',   utm_campaign: 'workshop-event' },
  { label: 'School',           utm_medium: 'qr', utm_source: 'school',     utm_campaign: 'school-program' },
  { label: 'Chamber Event',    utm_medium: 'qr', utm_source: 'chamber',    utm_campaign: 'chamber-event' },
  { label: 'Speaking',         utm_medium: 'qr', utm_source: 'speaking',   utm_campaign: 'speaking-engagement' },
  { label: 'Conference Booth', utm_medium: 'qr', utm_source: 'conference', utm_campaign: 'conference-booth' },
  { label: 'Podcast',          utm_medium: 'qr', utm_source: 'podcast',    utm_campaign: 'podcast-appearance' },
];

export default function DiscQuizLeadsManager() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [styleFilter, setStyleFilter] = useState('');
  const [showQrPanel, setShowQrPanel] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes, attrRes] = await Promise.all([
        axios.get(`${API}/api/disc-quiz/admin/leads`, {
          headers: getAuthHeaders(),
          params: styleFilter ? { style: styleFilter } : {},
        }),
        axios.get(`${API}/api/disc-quiz/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/disc-quiz/admin/attribution-stats`, { headers: getAuthHeaders() }),
      ]);
      setLeads(leadsRes.data.items || []);
      setStats(statsRes.data);
      setAttribution(attrRes.data);
    } catch (err) {
      console.error('[DISC Admin] load failed:', err?.message);
      toast.error('Failed to load DISC quiz leads');
    } finally {
      setLoading(false);
    }
  }, [styleFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete ${lead.first_name} ${lead.last_name}'s quiz lead?`)) return;
    try {
      await axios.delete(`${API}/api/disc-quiz/admin/leads/${lead.id}`, { headers: getAuthHeaders() });
      toast.success('Lead deleted');
      fetchAll();
    } catch (err) {
      console.error('[DISC Admin] delete failed:', err?.message);
      toast.error('Delete failed');
    }
  };

  const exportCsv = () => {
    if (leads.length === 0) { toast.error('No leads to export'); return; }
    const rows = [
      ['First Name', 'Last Name', 'Email', 'Phone', 'Organization', 'Role', 'Primary Style', 'Secondary Style', 'CTA Clicked', 'PDF Downloads', 'UTM Source', 'UTM Campaign', 'Date'],
      ...leads.map((l) => [
        l.first_name, l.last_name, l.email, l.phone || '', l.organization || '', l.role || '',
        l.primary_style, l.secondary_style || '', l.cta_clicked ? 'Yes' : 'No',
        l.guide_download_count || 0, l.utm_source || '', l.utm_campaign || '', l.created_at,
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `disc-quiz-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const filtered = leads.filter((l) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [l.first_name, l.last_name, l.email, l.organization, l.role]
      .filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="disc-quiz-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Find Your DISC Bird — Quiz Leads</h2>
          <p className="text-sm text-slate-400 mt-1">
            Mini-assessment lead generator. Track quiz takers, their styles, PDF downloads, and full-assessment upgrade clicks (not purchases).
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowQrPanel((s) => !s)} variant="outline" size="sm" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10" data-testid="disc-qr-toggle-btn">
            <QrCode className="h-4 w-4 mr-2" /> {showQrPanel ? 'Hide' : 'Generate'} QR Code
          </Button>
          <Button onClick={fetchAll} variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="disc-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button onClick={exportCsv} size="sm" className="bg-gradient-to-r from-violet-600 to-cyan-500 text-white" data-testid="disc-export-btn">
            Export CSV
          </Button>
        </div>
      </div>

      {/* QR Code panel */}
      {showQrPanel && <QrCodeGenerator />}

      {/* Top-level analytics row */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
          <StatBox label="Total Quiz Takers" value={stats.total_leads} accent="text-white" icon={<BarChart3 className="h-3.5 w-3.5" />} />
          <StatBox label="Started, Not Finished" value={stats.started_not_finished ?? 0} accent="text-slate-300" icon={<BarChart3 className="h-3.5 w-3.5" />} />
          <StatBox label="Upgrade Clicks" value={stats.cta_clicked} accent="text-emerald-400" icon={<MousePointerClick className="h-3.5 w-3.5" />} />
          <StatBox label="Upgrade Click Rate" value={`${stats.conversion_rate}%`} accent="text-cyan-400" icon={<TrendingUp className="h-3.5 w-3.5" />} />
          <StatBox label="PDF Downloads" value={stats.guide_downloads} accent="text-amber-400" icon={<FileDown className="h-3.5 w-3.5" />} />
          <StatBox label="Result Emails" value={stats.result_emails_sent} accent="text-violet-400" icon={<MailCheck className="h-3.5 w-3.5" />} />
          <StatBox label="Workshop Inquiries" value={stats.workshop_inquiries_from_quiz} accent="text-pink-400" />
        </div>
      )}

      {stats?.upgrade_clicks_by_location && (
        <div className="mb-6 rounded-lg border border-slate-800 bg-slate-900/50 px-4 py-3 text-xs text-slate-400 space-y-1" data-testid="disc-upgrade-by-location">
          <div className="text-slate-500 uppercase tracking-wider">Upgrade clicks by location (clicks only, not purchases)</div>
          {Object.entries(stats.upgrade_clicks_by_location).map(([loc, byBird]) => (
            <div key={loc} data-testid={`disc-upgrade-loc-${loc}`}>
              <span className="text-slate-300 font-medium">{loc === 'mobile_sticky' ? 'Mobile sticky bar' : 'Results upgrade section'}:</span>{' '}
              {Object.keys(byBird).length ? Object.entries(byBird).map(([b, n]) => `${b} ${n}`).join(' · ') : 'none yet'}
            </div>
          ))}
        </div>
      )}

      {/* Bird-style breakdown (clickable filter pills) */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {Object.entries(stats.by_style || {}).map(([style, count]) => {
            const b = BIRD_BADGE[style] || {};
            const conv = stats.style_conversion?.[style];
            return (
              <button
                key={style}
                onClick={() => setStyleFilter(styleFilter === style ? '' : style)}
                className={`rounded-lg border px-4 py-3 text-left transition-all ${
                  styleFilter === style
                    ? 'border-violet-500 bg-violet-500/10'
                    : 'border-slate-800 bg-slate-900/50 hover:border-slate-700'
                }`}
                data-testid={`disc-stat-${style.toLowerCase()}`}
              >
                <div className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <BirdMascot name={style} className="h-5 w-5" /> {style}
                </div>
                <div className={`text-2xl font-bold mt-1 ${b.text || 'text-white'}`}>{count}</div>
                {conv && conv.total > 0 && (
                  <div className="text-[11px] text-slate-500 mt-1">
                    {conv.cta_clicked} upgrade clicks · {conv.conversion_rate}%
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Nurture queue indicator */}
      {stats?.nurture && (
        <div className="mb-6 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className="text-slate-400 font-semibold uppercase text-xs tracking-wider">Nurture Sequence:</span>
          <span className="text-amber-300">{stats.nurture.pending} pending</span>
          <span className="text-emerald-300">{stats.nurture.sent} sent</span>
          <span className="text-red-300">{stats.nurture.failed} failed</span>
        </div>
      )}

      {/* Attribution stats */}
      {attribution && (
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <AttributionTable title="Top Traffic Sources" rows={attribution.by_source} testid="attr-source" />
          <AttributionTable title="Top Campaigns" rows={attribution.by_campaign} testid="attr-campaign" />
        </div>
      )}

      {/* Conversion-by-Source × Bird Heatmap */}
      <SourceBirdHeatmap />

      {/* Search + filter pill */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, organization…"
          className="max-w-md bg-slate-900 border-slate-700 text-white"
          data-testid="disc-search"
        />
        {styleFilter && (
          <button
            onClick={() => setStyleFilter('')}
            className="text-xs text-violet-400 hover:text-violet-300 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/30"
            data-testid="disc-clear-filter"
          >
            Filter: {styleFilter} ×
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/50">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-900/80 text-slate-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Contact</th>
              <th className="px-4 py-3 text-left">Org / Role</th>
              <th className="px-4 py-3 text-left">Style</th>
              <th className="px-4 py-3 text-center">CTA</th>
              <th className="px-4 py-3 text-center">PDF</th>
              <th className="px-4 py-3 text-left">Source</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center text-slate-500 py-10">No quiz leads yet. Share /find-your-bird to start collecting leads.</td></tr>
            )}
            {filtered.map((l) => {
              const badge = BIRD_BADGE[l.primary_style] || BIRD_BADGE.Eagle;
              return (
                <tr key={l.id} className="hover:bg-slate-800/30 transition-colors" data-testid={`disc-row-${l.email}`}>
                  <td className="px-4 py-3 text-white font-semibold">
                    {l.first_name} {l.last_name}
                  </td>
                  <td className="px-4 py-3 text-slate-300">
                    <div className="flex items-center gap-1.5 text-xs"><Mail className="h-3 w-3" /> {l.email}</div>
                    {l.phone && <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1"><Phone className="h-3 w-3" /> {l.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-xs">
                    {l.organization && <div className="flex items-center gap-1.5"><Building2 className="h-3 w-3" /> {l.organization}</div>}
                    {l.role && <div className="text-slate-500 mt-1">{l.role}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${badge.bg} ${badge.text}`}>
                        <BirdMascot name={l.primary_style} className="h-4 w-4" /> {l.primary_style}
                      </span>
                      {l.secondary_style && (
                        <span className="text-xs text-slate-500">+ {l.secondary_style}</span>
                      )}
                      {l.tied && (
                        <span className="text-[10px] uppercase font-bold text-amber-400">Tied</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {l.cta_clicked
                      ? <MousePointerClick className="h-4 w-4 text-emerald-400 mx-auto" />
                      : <span className="text-slate-700">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center text-xs">
                    {(l.guide_download_count || 0) > 0
                      ? <span className="inline-flex items-center gap-1 text-amber-400 font-semibold"><FileDown className="h-3.5 w-3.5" /> {l.guide_download_count}</span>
                      : <span className="text-slate-700">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {l.utm_source || l.utm_campaign ? (
                      <div className="flex flex-col">
                        <span className="text-cyan-400">{l.utm_source || '—'}</span>
                        <span className="text-slate-500">{l.utm_campaign || ''}</span>
                      </div>
                    ) : (
                      <span>{l.source_page || '/find-your-bird'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {new Date(l.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDelete(l)}
                      className="p-2 rounded hover:bg-slate-800 text-slate-400 hover:text-red-400"
                      data-testid={`disc-delete-${l.email}`}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatBox({ label, value, accent = 'text-white', icon }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/50 px-4 py-3">
      <div className="text-[11px] text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className={`text-2xl font-bold ${accent} mt-1`}>{value}</div>
    </div>
  );
}

function AttributionTable({ title, rows, testid }) {
  const top = (rows || []).slice(0, 5);
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4" data-testid={testid}>
      <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3">{title}</h3>
      {top.length === 0 ? (
        <p className="text-xs text-slate-500">No data yet.</p>
      ) : (
        <table className="w-full text-xs">
          <tbody className="divide-y divide-slate-800">
            {top.map((r, i) => (
              <tr key={r.value + i}>
                <td className="py-2 text-slate-300 truncate max-w-[180px]">{r.value}</td>
                <td className="py-2 text-right text-white font-semibold">{r.count}</td>
                <td className="py-2 text-right text-emerald-400 w-16">{r.conversion_rate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function QrCodeGenerator() {
  const baseUrl = `${window.location.origin}/find-your-bird`;
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('qr');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmContent, setUtmContent] = useState('');
  const [qrUrl, setQrUrl] = useState(null);
  const [finalUrl, setFinalUrl] = useState('');
  const [generating, setGenerating] = useState(false);

  const applyPreset = (preset) => {
    setUtmSource(preset.utm_source);
    setUtmMedium(preset.utm_medium);
    setUtmCampaign(preset.utm_campaign);
  };

  const buildFinalUrl = () => {
    const params = new URLSearchParams();
    if (utmSource) params.set('utm_source', utmSource);
    if (utmMedium) params.set('utm_medium', utmMedium);
    if (utmCampaign) params.set('utm_campaign', utmCampaign);
    if (utmContent) params.set('utm_content', utmContent);
    const qs = params.toString();
    return qs ? `${baseUrl}?${qs}` : baseUrl;
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const params = {
        url: baseUrl,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_content: utmContent,
      };
      const res = await axios.get(`${API}/api/disc-quiz/admin/qr-code`, {
        headers: getAuthHeaders(),
        params,
        responseType: 'blob',
      });
      // Revoke any previous URL
      if (qrUrl) URL.revokeObjectURL(qrUrl);
      const blobUrl = URL.createObjectURL(res.data);
      setQrUrl(blobUrl);
      // The backend returns the final URL in X-QR-URL header
      setFinalUrl(res.headers['x-qr-url'] || buildFinalUrl());
      toast.success('QR code generated');
    } catch (err) {
      console.error('[QR Gen] failed:', err?.message);
      toast.error('Failed to generate QR code');
    } finally {
      setGenerating(false);
    }
  };

  const downloadQr = () => {
    if (!qrUrl) return;
    const a = document.createElement('a');
    a.href = qrUrl;
    a.download = `disc-bird-qr-${utmCampaign || utmSource || 'find-your-bird'}.png`;
    a.click();
  };

  return (
    <div className="mb-6 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-slate-900/40 p-5" data-testid="qr-code-generator">
      <h3 className="text-base font-bold text-white mb-1 flex items-center gap-2">
        <QrCode className="h-4 w-4 text-amber-300" />
        QR Code Generator
      </h3>
      <p className="text-xs text-slate-400 mb-4">
        Create UTM-tagged QR codes for workshops, schools, chamber events, conferences, podcasts, and speaking engagements. Every scan is attributed back to its source.
      </p>

      {/* Presets */}
      <div className="flex flex-wrap gap-2 mb-4">
        {QR_PRESETS.map((p) => (
          <button
            key={p.label}
            onClick={() => applyPreset(p)}
            className="text-xs px-3 py-1.5 rounded-full bg-slate-800 hover:bg-amber-500/20 text-slate-300 hover:text-amber-200 border border-slate-700 hover:border-amber-500/40 transition-colors"
            data-testid={`qr-preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Form */}
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">UTM Source</Label>
            <Input value={utmSource} onChange={(e) => setUtmSource(e.target.value)} placeholder="e.g. workshop, school, chamber"
              className="bg-slate-900 border-slate-700 text-white text-sm" data-testid="qr-utm-source" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">UTM Medium</Label>
            <Input value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder="qr"
              className="bg-slate-900 border-slate-700 text-white text-sm" data-testid="qr-utm-medium" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">UTM Campaign</Label>
            <Input value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} placeholder="e.g. ccsd-leadership-jan26"
              className="bg-slate-900 border-slate-700 text-white text-sm" data-testid="qr-utm-campaign" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">UTM Content <span className="text-slate-600">(optional)</span></Label>
            <Input value={utmContent} onChange={(e) => setUtmContent(e.target.value)} placeholder="slide-13, booth-banner, etc."
              className="bg-slate-900 border-slate-700 text-white text-sm" data-testid="qr-utm-content" />
          </div>
          <Button onClick={generate} disabled={generating} className="bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 font-bold w-full" data-testid="qr-generate-btn">
            {generating ? 'Generating…' : 'Generate QR Code'}
          </Button>
        </div>

        {/* Preview */}
        <div className="flex flex-col items-center justify-center bg-slate-950/50 rounded-lg border border-slate-800 p-4">
          {qrUrl ? (
            <>
              <img src={qrUrl} alt="QR Code" className="w-48 h-48 bg-white p-3 rounded-lg" data-testid="qr-preview-img" />
              <div className="mt-3 text-[11px] text-slate-400 text-center break-all max-w-full" data-testid="qr-final-url">
                {finalUrl}
              </div>
              <Button onClick={downloadQr} variant="outline" size="sm" className="mt-3 border-amber-500/40 text-amber-300 hover:bg-amber-500/10" data-testid="qr-download-btn">
                <Download className="h-4 w-4 mr-2" /> Download PNG
              </Button>
            </>
          ) : (
            <div className="text-center text-slate-600 text-sm py-12">
              <QrCode className="h-16 w-16 mx-auto opacity-30 mb-2" />
              Configure your UTM tags and click Generate.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Conversion-by-Source × Bird Heatmap ─────────────────────────────────────
function SourceBirdHeatmap() {
  const [data, setData] = useState(null);
  const [dimension, setDimension] = useState('utm_source');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/disc-quiz/admin/source-bird-matrix`, {
        headers: getAuthHeaders(),
        params: { dimension },
      });
      setData(res.data);
    } catch (err) {
      console.error('[Heatmap] failed:', err?.message);
      toast.error('Failed to load heatmap');
    } finally {
      setLoading(false);
    }
  }, [dimension]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="mb-6 rounded-xl border border-slate-800 bg-slate-900/40 p-5">
        <div className="h-32 flex items-center justify-center text-slate-500 text-sm">Loading heatmap…</div>
      </div>
    );
  }

  const { birds, sources, column_totals, grand_total } = data;
  const maxCount = Math.max(1, ...sources.flatMap((s) => birds.map((b) => s.by_bird[b].count)));

  // Heat intensity by raw count (0..maxCount → opacity 0..0.85)
  const cellBg = (bird, count) => {
    const intensity = count / maxCount;
    const colors = {
      Eagle:  `rgba(225, 107, 63, ${0.1 + intensity * 0.75})`,
      Parrot: `rgba(242, 193, 78, ${0.1 + intensity * 0.75})`,
      Dove:   `rgba(79, 182, 201, ${0.1 + intensity * 0.75})`,
      Owl:    `rgba(99, 102, 241, ${0.1 + intensity * 0.75})`,
    };
    return count > 0 ? colors[bird] : 'transparent';
  };

  return (
    <div className="mb-6 rounded-xl border border-violet-500/30 bg-gradient-to-br from-violet-500/5 to-slate-900/40 p-5" data-testid="source-bird-heatmap">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <span className="text-violet-300">Upgrade Clicks by Source × Bird</span>
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Which audiences produce which DISC styles. Use this to tailor workshops, speaking topics, and partnership targeting.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'utm_source', label: 'By Source' },
            { key: 'utm_campaign', label: 'By Campaign' },
            { key: 'utm_medium', label: 'By Medium' },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setDimension(opt.key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                dimension === opt.key
                  ? 'bg-violet-500/20 border-violet-500 text-violet-200'
                  : 'border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-300'
              }`}
              data-testid={`heatmap-dim-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-32 flex items-center justify-center text-slate-500 text-sm">Loading…</div>
      ) : sources.length === 0 ? (
        <div className="py-10 text-center text-slate-500 text-sm">
          No data yet. As leads come in with UTM tags, this matrix will fill up.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="heatmap-table">
            <thead>
              <tr className="text-slate-400">
                <th className="text-left font-semibold uppercase tracking-wider py-2 px-2 sticky left-0 bg-slate-900/40">{dimension.replace('utm_', '')}</th>
                {birds.map((b) => (
                  <th key={b} className="px-3 py-2 font-semibold uppercase tracking-wider">
                    <div className="flex flex-col items-center gap-0.5">
                      <span>{b}</span>
                      <span className="text-[10px] text-slate-600 font-normal normal-case">({column_totals[b]})</span>
                    </div>
                  </th>
                ))}
                <th className="px-3 py-2 font-semibold uppercase tracking-wider text-right">Total</th>
                <th className="px-3 py-2 font-semibold uppercase tracking-wider text-right">CTA %</th>
                <th className="px-3 py-2 font-semibold uppercase tracking-wider text-left">Dominant</th>
                <th className="px-3 py-2 font-semibold uppercase tracking-wider text-right">Briefing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {sources.map((row) => (
                <tr key={row.source} className="hover:bg-slate-800/20" data-testid={`heatmap-row-${row.source}`}>
                  <td className="py-2 px-2 text-slate-300 font-semibold truncate max-w-[200px] sticky left-0 bg-slate-900/40">
                    {row.source}
                  </td>
                  {birds.map((b) => {
                    const cell = row.by_bird[b];
                    return (
                      <td
                        key={b}
                        className="text-center px-3 py-2 transition-colors"
                        style={{ background: cellBg(b, cell.count) }}
                        title={`${cell.count} leads (${cell.share}%) — ${cell.cta_clicked} upgrade clicks`}
                      >
                        {cell.count > 0 ? (
                          <div className="flex flex-col items-center">
                            <span className="text-white font-bold">{cell.count}</span>
                            <span className="text-[10px] text-slate-300/80">{cell.share}%</span>
                          </div>
                        ) : (
                          <span className="text-slate-700">·</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="text-right px-3 py-2 text-white font-bold">{row.total}</td>
                  <td className="text-right px-3 py-2 text-emerald-400">{row.conversion_rate}%</td>
                  <td className="text-left px-3 py-2">
                    {row.dominant_bird && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${
                        BIRD_BADGE[row.dominant_bird]?.bg || ''} ${BIRD_BADGE[row.dominant_bird]?.text || ''}`}>
                        <BirdMascot name={row.dominant_bird} className="h-3.5 w-3.5" /> {row.dominant_bird}
                      </span>
                    )}
                  </td>
                  <td className="text-right px-3 py-2">
                    <BriefingActions row={row} dimension={dimension} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-700 text-slate-300">
                <td className="py-2 px-2 font-bold sticky left-0 bg-slate-900/40">All Sources</td>
                {birds.map((b) => (
                  <td key={b} className="text-center px-3 py-2 font-bold text-white">
                    {column_totals[b]}
                  </td>
                ))}
                <td className="text-right px-3 py-2 font-bold text-white">{grand_total}</td>
                <td colSpan={3}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}


// ─── Briefing Actions: PDF preview + Email modal trigger ─────────────────────
function BriefingActions({ row, dimension }) {
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const previewPdf = async () => {
    try {
      const res = await axios.get(
        `${API}/api/disc-quiz/admin/workshop-briefing`,
        {
          headers: getAuthHeaders(),
          params: { source: row.source, dimension },
          responseType: 'blob',
        },
      );
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('[Briefing PDF] failed:', err?.message);
      toast.error('Briefing generation failed');
    }
  };

  return (
    <>
      <div className="inline-flex items-center gap-1">
        <button
          onClick={previewPdf}
          title="Open Briefing PDF"
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/30 text-violet-300 hover:text-violet-200 text-[11px] font-semibold"
          data-testid={`briefing-pdf-btn-${row.source}`}
        >
          <FileDown className="h-3 w-3" /> PDF
        </button>
        <button
          onClick={() => setEmailModalOpen(true)}
          title="Email this briefing"
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 hover:text-cyan-200 text-[11px] font-semibold"
          data-testid={`briefing-email-btn-${row.source}`}
        >
          <Send className="h-3 w-3" /> Email
        </button>
      </div>
      {emailModalOpen && (
        <BriefingEmailModal
          source={row.source}
          dimension={dimension}
          onClose={() => setEmailModalOpen(false)}
        />
      )}
    </>
  );
}

// ─── Email Briefing Modal ────────────────────────────────────────────────────
function BriefingEmailModal({ source, dimension, onClose }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [segment, setSegment] = useState(null);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [includePdf, setIncludePdf] = useState(true);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [previewRes, historyRes] = await Promise.all([
          axios.get(`${API}/api/disc-quiz/admin/workshop-briefing/preview`, {
            headers: getAuthHeaders(),
            params: { source, dimension },
          }),
          axios.get(`${API}/api/disc-quiz/admin/workshop-briefing/history`, {
            headers: getAuthHeaders(),
            params: { source, limit: 5 },
          }),
        ]);
        if (cancelled) return;
        setSegment(previewRes.data.segment);
        setSubject(previewRes.data.subject || '');
        setBodyHtml(previewRes.data.body_html || '');
        setHistory(historyRes.data.items || []);
      } catch (err) {
        console.error('[Briefing modal] load failed:', err?.message);
        toast.error('Could not load briefing preview');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [source, dimension, onClose]);

  const splitEmails = (s) => s.split(/[,;\n]+/).map((e) => e.trim()).filter(Boolean);

  const handleSend = async () => {
    const toList = splitEmails(to);
    if (toList.length === 0) {
      toast.error('Add at least one recipient in To');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/disc-quiz/admin/workshop-briefing/email`, {
        source, dimension,
        to: toList,
        cc: splitEmails(cc),
        bcc: splitEmails(bcc),
        subject,
        body_html: bodyHtml,
        include_pdf: includePdf,
      }, { headers: getAuthHeaders() });
      if (res.data?.ok) {
        toast.success(`Briefing sent to ${toList.length} recipient${toList.length === 1 ? '' : 's'}`);
        onClose();
      } else {
        toast.error('Send failed: ' + (res.data?.error || 'unknown error'));
      }
    } catch (err) {
      console.error('[Briefing send] failed:', err?.response?.data || err?.message);
      toast.error('Send failed: ' + (err?.response?.data?.detail || err?.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="briefing-email-modal"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 px-6 py-4 flex items-start justify-between z-10">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Send className="h-4 w-4 text-cyan-400" />
              Email Workshop Briefing
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              <span className="text-slate-300 font-semibold">{source}</span>
              {segment && (
                <>
                  {' · '}
                  <span className="text-emerald-400">{segment.total} quiz takers</span>
                  {' · '}
                  <span className="text-violet-300">Dominant: {segment.dominant_bird}</span>
                  {' · '}
                  <span className="text-cyan-300">{segment.conversion_rate}% conversion</span>
                </>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="briefing-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Loading preview…</div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div>
              <Label className="text-xs text-slate-400">To <span className="text-red-400">*</span></Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)}
                placeholder="alice@example.com, bob@example.com"
                className="bg-slate-950 border-slate-700 text-white text-sm"
                data-testid="briefing-modal-to" />
              <p className="text-[11px] text-slate-600 mt-1">Comma- or semicolon-separated</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Cc</Label>
                <Input value={cc} onChange={(e) => setCc(e.target.value)}
                  className="bg-slate-950 border-slate-700 text-white text-sm" data-testid="briefing-modal-cc" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Bcc</Label>
                <Input value={bcc} onChange={(e) => setBcc(e.target.value)}
                  className="bg-slate-950 border-slate-700 text-white text-sm" data-testid="briefing-modal-bcc" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                className="bg-slate-950 border-slate-700 text-white text-sm" data-testid="briefing-modal-subject" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Message (HTML)</Label>
              <Textarea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)}
                rows={10}
                className="bg-slate-950 border-slate-700 text-white text-sm font-mono"
                data-testid="briefing-modal-body" />
              <p className="text-[11px] text-slate-600 mt-1">Pre-filled with audience context. Edit freely — supports inline HTML.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={includePdf}
                onChange={(e) => setIncludePdf(e.target.checked)}
                className="rounded border-slate-600 text-cyan-500 focus:ring-cyan-500 bg-slate-800"
                data-testid="briefing-modal-include-pdf"
              />
              Attach the briefing PDF
            </label>

            {/* Send history */}
            {history.length > 0 && (
              <div className="rounded-lg border border-slate-700 bg-slate-950/50 p-3" data-testid="briefing-modal-history">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <History className="h-3.5 w-3.5" /> Recent Sends ({history.length})
                </h4>
                <div className="space-y-2">
                  {history.map((h) => (
                    <div key={h.id} className="flex items-start justify-between gap-3 text-xs">
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-300 truncate" title={h.subject}>{h.subject}</div>
                        <div className="text-slate-600 text-[11px]">
                          To {h.to.join(', ')} · {new Date(h.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] flex-shrink-0">
                        <span className={`px-1.5 py-0.5 rounded ${h.status === 'sent' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                          {h.status}
                        </span>
                        <span className="text-amber-400 inline-flex items-center gap-1"><Eye className="h-3 w-3" />{h.opens}</span>
                        <span className="text-cyan-400 inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" />{h.clicks}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900 border-t border-slate-700 px-6 py-3 flex justify-end gap-2">
          <Button onClick={onClose} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="briefing-modal-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || loading}
            className="bg-gradient-to-r from-cyan-500 to-violet-500 text-white font-bold"
            data-testid="briefing-modal-send"
          >
            <Send className="h-4 w-4 mr-2" />
            {sending ? 'Sending…' : 'Send Briefing'}
          </Button>
        </div>
      </div>
    </div>
  );
}

